import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import IconEmailMarketingAllTimes from 'assets/icon-email-marketing-all-times.svg';
import IconTotalSales from 'assets/mainPage/total-sales.svg';
import IconDelivered from 'assets/icon-em-delivered.svg';
import IconOpenRate from 'assets/icon-em-open-rate.svg';
import IconClickRate from 'assets/icon-em-click-rate.svg';
import {
  EmailMarketingSpinner,
  StatCard,
  StatCardBody,
  StatCardHeader,
  StatCardDelta,
  StatCardHint,
  StatCardTitle,
  StatCardValue,
  StatCardsRow
} from 'routes/App/emailMarketingV2/components/styles';
import {
  GET_MARKETING_STATISTIC,
  SET_EMAIL_MARKETING_RESULT_PAGE_SPINNER
} from 'store/emailMarketing/action';
import Loading from 'components/shared/Utils/Loading';
import {formatRateShort, formatCount} from 'helpers/constants/emailMarketing';

const formatMoney = (value) =>
  `$${new Intl.NumberFormat('en-GB', {maximumFractionDigits: 0}).format(
    value || 0
  )}`;

const StatCardItem = ({icon, title, value, delta, hint}) => (
  <StatCard>
    <StatCardHeader>
      <img src={icon} alt='' />
      <StatCardTitle>{title}</StatCardTitle>
    </StatCardHeader>
    <StatCardBody>
      <StatCardValue>
        {value}
        {delta && <StatCardDelta>{delta}</StatCardDelta>}
      </StatCardValue>
      {hint && <StatCardHint>{hint}</StatCardHint>}
    </StatCardBody>
  </StatCard>
);

StatCardItem.propTypes = {
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  delta: PropTypes.string,
  hint: PropTypes.string
};

StatCardItem.defaultProps = {
  delta: null,
  hint: null
};

const ResultPage = () => {
  const dispatch = useDispatch();
  const {statistic, spinner, campaignsSummary} = useSelector(
    (state) => state.emailMarketingReducer
  );
  const {locationId} = useSelector((state) => state.user);

  useEffect(() => {
    if (locationId) {
      dispatch(SET_EMAIL_MARKETING_RESULT_PAGE_SPINNER(true));
      dispatch(GET_MARKETING_STATISTIC());
    }
  }, [locationId]);

  if (spinner) {
    return (
      <EmailMarketingSpinner>
        <Loading />
      </EmailMarketingSpinner>
    );
  }

  const allTime = statistic.allSummary || {};
  const lastMonth = statistic.lastMonthSummary || {};

  // The previous window may cover fewer upstreams than lastMonthSummary, so it is
  // compared against comparableLastMonth, which is restricted to the same ones.
  const previousMonth = statistic.previousMonthSummary;
  const comparableLastMonth = previousMonth?.comparableLastMonth;
  const salesDelta =
    previousMonth?.salesDriven && comparableLastMonth
      ? (comparableLastMonth.salesDriven - previousMonth.salesDriven) /
        previousMonth.salesDriven
      : null;
  const salesDeltaLabel =
    salesDelta === null
      ? null
      : `${salesDelta >= 0 ? '+' : ''}${(salesDelta * 100).toFixed(0)}%`;

  return (
    <>
      <StatCardsRow>
        <StatCardItem
          icon={IconEmailMarketingAllTimes}
          title='Sales Driven - All Time'
          value={formatMoney(allTime.salesDriven)}
          hint='From guests who ordered within 30 days of receiving an email'
        />
        <StatCardItem
          icon={IconTotalSales}
          title='Sales Driven - Last 30 Days'
          value={formatMoney(lastMonth.salesDriven)}
          delta={salesDeltaLabel ? `(${salesDeltaLabel})` : null}
          hint={
            salesDeltaLabel ? `${salesDeltaLabel} vs. previous 30 days` : null
          }
        />
      </StatCardsRow>

      <StatCardsRow className='three'>
        <StatCardItem
          icon={IconDelivered}
          title='Emails Delivered'
          value={formatCount(lastMonth.delivered)}
          hint='Last 30 days'
        />
        <StatCardItem
          icon={IconOpenRate}
          title='Avg. Open Rate'
          value={formatRateShort(campaignsSummary?.avgOpenRate?.last30d)}
          hint='Last 30 days'
        />
        <StatCardItem
          icon={IconClickRate}
          title='Avg. Click Rate'
          value={formatRateShort(campaignsSummary?.avgClickRate?.last30d)}
          hint='Last 30 days'
        />
      </StatCardsRow>
    </>
  );
};

export default ResultPage;
